import { CdkTableModule } from "@angular/cdk/table";
import { Component, inject, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { InformationalType } from "@shared/interfaces/informational.interface";
import { InformationalService } from "@shared/services/informational.service";
import { catchError, filter, finalize, of, take, tap } from "rxjs";
import { LoginService } from "../../../../core/services/login.service";
import { NotifierService } from "../../../../core/services/notifier.service";
import { InformationalModalComponent } from "../../../../shared/components/informational-modal/informational-modal.component";
import { NewFeatureModalComponent } from "../../../../shared/components/new-feature-modal/new-feature-modal.component";
import { AppMaterialModule } from "../../../../shared/material/app-material.module";

@Component({
  selector: "app-login-broadfactor",
  standalone: true,
  imports: [ReactiveFormsModule, AppMaterialModule, CdkTableModule],
  templateUrl: "./login-broadfactor.component.html",
  styleUrl: "./login-broadfactor.component.scss",
})
export class LoginBroadfactorComponent {
  fb = inject(FormBuilder);
  router = inject(Router);
  loginService = inject(LoginService);
  notifier = inject(NotifierService);
  matDialog = inject(MatDialog);
  informationalService = inject(InformationalService);

  loginForm!: FormGroup;
  isLoading = signal(false);
  hide = signal(true);

  constructor() {
    this.createFormGroup();
  }

  createFormGroup(): void {
    this.loginForm = this.fb.group({
      userName: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required, Validators.minLength(8)]],
    });
  }

  navigateToForgotPassword(): void {
    this.router.navigateByUrl("esqueci-minha-senha");
  }

  openInformationalModal(): void {
    const dialogConfig: MatDialogConfig = {
      disableClose: true,
    };
    const dialogRef: MatDialogRef<InformationalModalComponent> = this.matDialog.open(
      InformationalModalComponent,
      dialogConfig,
    );

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        tap(viewed => {
          if (viewed) {
            this.handleDismissInformationalModal();
          }
        }),
      )
      .subscribe(() => { });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.isLoading.set(true);
    this.loginService
      .auth(this.loginForm.value)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.router.events
            .pipe(
              filter((event) => event instanceof NavigationEnd),
              take(1)
            )
            .subscribe((event) => {
              const url = event.urlAfterRedirects.split("?")[0];
              const isAuthenticatedArea =
                url === "/comprador" ||
                url.startsWith("/comprador/") ||
                url === "/cedente" ||
                url.startsWith("/cedente/");

              if (isAuthenticatedArea) {
                // this.handleShowNewsBanner(); // Banner de masterclass desativado.
              }
            });
        },
        error: () => {
          this.notifier.showNotification({
            message: "Falha ao tentar efetuar o login",
            alternative: "Credenciais enviadas são inválidas ou estão expiradas",
            messageType: "errorNotifier"
          });
        },
      });
  }

  handleHidePassword(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  handleShowNewsBanner(): void {
    if (sessionStorage.getItem("newsBannerShown")) {
      this.handleShowInformationalModal();
      return;
    }

    const dialogRef = this.matDialog.open(NewFeatureModalComponent, {
      width: "1400px",
      maxWidth: "95vw",
      height: "auto",
      maxHeight: "none",
      disableClose: true,
      backdropClass: "news-modal-backdrop",
      panelClass: "news-modal-panel"
    });

    sessionStorage.setItem("newsBannerShown", "true");
    dialogRef.backdropClick().pipe(take(1)).subscribe(() => {
      dialogRef.componentInstance.close();
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      this.handleShowInformationalModal();
    });
  }

  handleShowInformationalModal(): void {
    const companyId = this.loginService.authToken.value?.companyId;
    const isLead = this.loginService.authToken.value?.companyType === "LEAD";

    if (isLead) {
      return;
    }

    this.informationalService
      .hasViewed(InformationalType["INTEREST_RATE"], companyId)
      .pipe(
        take(1),
        catchError(() => of(false)),
        tap(hasViewed => {
          if (!hasViewed) {
            this.openInformationalModal();
          }
        }),
      )
      .subscribe();
  }

  handleDismissInformationalModal(): void {
    const companyId = this.loginService.authToken.value?.companyId;

    this.informationalService
      .markAsViewed(InformationalType["INTEREST_RATE"], companyId)
      .pipe(take(1))
      .subscribe();
  }
}
